/**
 * Which screen-taking surfaces are open, in one place.
 *
 * ─── The problem this replaces ─────────────────────────────────────────────
 * `index.ts` carried the same list five times, by hand:
 *
 *   if (dialog.isOpen || isVendorUIOpen() || isCurrencyShopUIOpen()
 *       || isBankerUIOpen() || isQuestUIOpen() || isShopUIOpen()) return;
 *
 * — in `interact()`, in `onWheel`, in `controls.isInputBlocked`, in the frame
 * loop's interaction prompt, and again (plus the paperdoll and the arena
 * modal) in the minimap's hide predicate. `blockedByPanel` had already been
 * extracted for the action bar's gates, which is four of the six, and that
 * extraction was written up at the time as fixing exactly this class of drift.
 * It did not cover the rest.
 *
 * The cost is not theoretical: adding ONE panel means finding five call sites,
 * and the failure mode of missing one is silent — the camera zooms behind an
 * open shop, or pressing E pops a conversation under a modal. Every new panel
 * in this batch (settings, home, crafting, …) would multiply that.
 *
 * ─── The shape ─────────────────────────────────────────────────────────────
 * A panel REGISTERS ITSELF when it is created and deregisters on dispose, so
 * adding one touches that panel's own module and nothing else. Registration
 * carries the two things callers actually ask about, because they are
 * genuinely different questions:
 *
 *  - `blocksWorldInput` — does this take the keyboard and mouse away from the
 *    world? True for a full-screen scrim. FALSE for the Survivor Record, which
 *    has never blocked movement and should not start.
 *  - `hidesMinimap` — does it cover enough that the minimap should yield?
 *    True for the record even though it does not block input.
 *  - `isModal` — is this a WINDOW, i.e. one of the surfaces only one of which
 *    may be on screen at a time? See below; this is deliberately a third flag
 *    and not a synonym for either of the two above.
 *
 * Registration is a MAP keyed by id, not a list, so a panel re-created across
 * an SPA remount (base-view mounts, tears down and mounts again) replaces its
 * own entry rather than accumulating stale predicates that answer for a
 * disposed element.
 *
 * ─── One window at a time (2026-09-18) ─────────────────────────────────────
 * Janeks: *"Option to show multiple menu modals at the same time. dont."*
 *
 * Nothing here used to enforce that, and the hole was `blocksWorldInput`
 * standing in for two different questions. The Survivor Record registers
 * `blocksWorldInput: false` — correctly, it has never stopped the player
 * walking — and `worldInputBlocked` therefore SKIPS it, so the action bar's
 * gates all answered "nothing is open" while it was on screen and every other
 * window opened on top of it. At z-index 15 it then sat UNDER their scrims.
 *
 * `isModal` is the missing question. It defaults to true, so a panel is a
 * window unless it says otherwise, and `closeOtherModals` is what every
 * `openXUI()` calls to make room for itself. The registry stays a registry:
 * it holds no z-order and no stack, it only knows who is open and how to ask
 * them to close.
 *
 * `close` is required for the same reason `isOpen` is — a registration that
 * cannot be closed cannot participate, and every panel in this codebase
 * already exports a closer.
 */

export interface PanelRegistration {
  /** Stable id, used to exempt a panel from its own gate. */
  id: string;
  isOpen: () => boolean;
  /**
   * Dismiss this panel. Called by `closeOtherModals` when another window opens,
   * so it MUST be safe to call on an already-closed panel — several are closed
   * twice on the dialog -> vendor path, which closes the conversation itself
   * before opening the stall.
   */
  close: () => void;
  /** Default true. Set false for a surface that deliberately leaves the world playable. */
  blocksWorldInput?: boolean;
  /** Default true. */
  hidesMinimap?: boolean;
  /**
   * Default true. Set false only for a surface that is meant to COEXIST with a
   * window — the chat box is the example, a corner box by design and not a
   * screen.
   */
  isModal?: boolean;
}

interface Entry extends Required<PanelRegistration> {}

const panels = new Map<string, Entry>();

/**
 * Register a panel. Returns its deregister function — call it from the
 * panel's `dispose`, or the registry keeps answering for a dead element.
 */
export function registerPanel(reg: PanelRegistration): () => void {
  const entry: Entry = {
    blocksWorldInput: true,
    hidesMinimap: true,
    isModal: true,
    ...reg,
  };
  panels.set(entry.id, entry);
  return () => {
    // Only if it is still OURS: a remount registers a fresh entry under the
    // same id, and the old teardown running afterwards must not delete it.
    if (panels.get(entry.id) === entry) panels.delete(entry.id);
  };
}

/**
 * Is the world's own input (movement, camera, click-to-interact) taken?
 *
 * `exceptId` exempts one panel from its own gate — what makes "press P again
 * to close the shop" work while P is otherwise blocked by an open panel.
 */
export function worldInputBlocked(exceptId?: string): boolean {
  for (const p of panels.values()) {
    if (!p.blocksWorldInput) continue;
    if (p.id === exceptId) continue;
    if (p.isOpen()) return true;
  }
  return false;
}

/**
 * Is a WINDOW on screen? Drives the menu column's z-index (`.bv-hud` goes
 * behind the scrim while one is up) and the few gates that are about windows
 * rather than about the world.
 *
 * Separate from `worldInputBlocked` on purpose — the Survivor Record answers
 * true here and false there.
 */
export function modalOpen(exceptId?: string): boolean {
  for (const p of panels.values()) {
    if (!p.isModal) continue;
    if (p.id === exceptId) continue;
    if (p.isOpen()) return true;
  }
  return false;
}

/**
 * Make room for the window that is opening: close every OTHER open modal.
 *
 * Called by each `openXUI()` as its first statement rather than from one gate
 * in `index.ts`, because a window is opened by four different routes — the
 * action bar, its hotkey, walking up to an NPC, and the HUD's own currency
 * chips — and only the panel itself sits on all four.
 *
 * Re-entrancy: a `close` that reopened something would loop. None do, and the
 * snapshot below means a close which deregistered its own panel mid-iteration
 * cannot corrupt the walk either.
 */
export function closeOtherModals(exceptId: string): void {
  for (const p of [...panels.values()]) {
    if (!p.isModal) continue;
    if (p.id === exceptId) continue;
    if (p.isOpen()) p.close();
  }
}

/** Should the minimap (and the gait indicator beside it) yield the corner? */
export function minimapBlocked(): boolean {
  for (const p of panels.values()) {
    if (p.hidesMinimap && p.isOpen()) return true;
  }
  return false;
}

/** Ids of everything open right now — for the debug overlay and for tests. */
export function openPanelIds(): string[] {
  return [...panels.values()].filter((p) => p.isOpen()).map((p) => p.id);
}

/** Drops every registration. Tests only — a real teardown deregisters its own. */
export function resetPanelRegistry(): void {
  panels.clear();
}
